// FeedItem -> log lines, e.g.
//   06:42:11  0x82f…9e1 found proof #881
//             … forged 4,000,000 FORGE for 0.0831 ETH
import type { FeedItem } from './api';
import { formatEth, formatTokens, formatWork } from './format';

export type Seg = { t: 'text'; v: string } | { t: 'addr'; v: string } | { t: 'num'; v: string };
export interface LogLine {
  key: string;
  segs: Seg[];
  tone?: 'bid' | 'ask' | 'quiet';
}

const txt = (v: string): Seg => ({ t: 'text', v });
const addr = (v: string | undefined): Seg => ({ t: 'addr', v: v ?? '' });
const num = (v: string): Seg => ({ t: 'num', v });
const big = (s: string | undefined) => (s ? BigInt(s) : 0n);

export function feedLines(item: FeedItem, withSymbol = false): LogLine[] {
  const sym = item.symbol;
  const pre: Seg[] = withSymbol ? [num(`$${sym}`), txt('  ')] : [];
  const k = item.id;
  switch (item.type) {
    case 'forge': {
      const total = big(item.cost) + big(item.fee);
      return [
        { key: `${k}a`, segs: [...pre, addr(item.actor), txt(` found proof #${item.challengeId ?? '?'}`)] },
        {
          key: `${k}b`,
          tone: 'bid',
          segs: [txt('… forged '), num(`${formatTokens(big(item.amount))} ${sym}`), txt(' for '), num(formatEth(total))],
        },
      ];
    }
    case 'sell':
      return [
        {
          key: k,
          tone: 'ask',
          segs: [...pre, addr(item.actor), txt(' sold '), num(`${formatTokens(big(item.amount))} ${sym}`), txt(' for '), num(formatEth(big(item.payout)))],
        },
      ];
    case 'challenge': {
      const lines: LogLine[] = [{ key: `${k}a`, tone: 'quiet', segs: [...pre, txt(`Challenge #${item.challengeId ?? '?'} opened`)] }];
      if (item.prevDifficultyFloat && item.difficultyFloat && item.prevDifficultyFloat !== item.difficultyFloat) {
        lines.push({
          key: `${k}b`,
          tone: 'quiet',
          segs: [txt('Difficulty retargeted '), num(formatWork(item.prevDifficultyFloat)), txt(' → '), num(formatWork(item.difficultyFloat))],
        });
      }
      return lines;
    }
    case 'graduated':
      return [{ key: k, segs: [...pre, txt('Graduated — pool '), addr(item.pair)] }];
    case 'launch':
      return [{ key: k, segs: [...pre, addr(item.actor), txt(` created $${sym}`)] }];
    case 'lp-fees':
      return [
        {
          key: k,
          tone: 'quiet',
          segs: [...pre, txt('LP fee growth collected: '), num(`${formatTokens(big(item.tokenAmount))} ${sym}`), txt(' + '), num(formatEth(big(item.quoteAmount))), txt(', split creator/protocol')],
        },
      ];
    case 'creator-fees':
      return [{ key: k, tone: 'quiet', segs: [...pre, txt('Creator fees paid: '), num(formatEth(big(item.amount)))] }];
    case 'protocol-fees':
      return [{ key: k, tone: 'quiet', segs: [...pre, txt('Protocol fees paid: '), num(formatEth(big(item.amount)))] }];
    case 'creator-changed':
      return [{ key: k, tone: 'quiet', segs: [...pre, txt('Creator changed to '), addr(item.actor)] }];
    case 'creator-handover':
      return [{ key: k, tone: 'quiet', segs: [...pre, addr(item.actor), txt(' offered the creator role to '), addr(item.pendingCreator)] }];
    default:
      return []; // event types added to the indexer later are skipped, not guessed at
  }
}

